import {Client as AuthClient} from '@apps-common/auth';
import {AuthProvider} from '@apps-common/auth-react';
import {setAuthToken as setGraneroAuthToken} from '@apps-common/geocorn-sdk/api';
import {setAuthToken} from '@apps-common/js-client/api/request';
import * as Sentry from '@sentry/react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {useLDClient} from 'launchdarkly-react-client-sdk';
import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router';
import {identify} from './analytics.js';
import {AUTH_CLIENT_ID, AUTH_ISSUER} from './config.js';
import {getUserLabKeys} from './constants/index.js';
import {setLabStatus} from '@actions/labs';
import AppLayout from '@app/components/AppLayout';

const authClient = new AuthClient({
  clientId: AUTH_CLIENT_ID,
  issuer: AUTH_ISSUER,
  redirectUri: `${window.location.origin}${import.meta.env.BASE_URL}`,
  postLogoutRedirectUri: `${window.location.origin}${import.meta.env.BASE_URL}`,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const SecureApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ldClient = useLDClient();

  const storedLabs = useSelector(state => state.labs);

  /**
   * @type {import('@apps-common/auth/client.js').ChangeListener}
   */
  const onAuthChange = useCallback(
    state => {
      setAuthToken(state.user?.accessToken || '');
      setGraneroAuthToken(state.user?.accessToken || '');

      if (state.user && !state.pending) {
        const {
          email,
          organization_id,
          organization_name,
          program_id,
          role_level,
          user_id,
        } = state.user.claims;

        identify({
          email,
          organization_id,
          organization_name,
          program_id,
          role_level,
          user_id,
        });

        Sentry.setUser({id: user_id, organization_id, program_id, email});

        // See https://launchdarkly.github.io/js-client-sdk/interfaces/LDMultiKindContext.html
        ldClient?.identify({
          kind: 'multi',
          user: {
            key: `user-${user_id}`,
            email,
            role_level,
            user_id,
            _meta: {
              privateAttributes: ['email'], //https://docs.launchdarkly.com/sdk/features/private-attributes
            },
          },
          organization: {
            key: `organization-${organization_id}`,
            organization_id,
            organization_name,
            program_id,
            _meta: {
              privateAttributes: ['organization_name'],
            },
          },
        });

        // Users may have labs enabled in localStorage that they shouldn't have access to
        // This can occur if they enabled the lab in a different account on the same device, or if they edited their local store
        // This checks permissions when the user logs in, and disables labs that shouldn't be available
        const userLabs = getUserLabKeys(state.user);

        for (const labKey in storedLabs) {
          if (!userLabs.includes(labKey)) {
            dispatch(setLabStatus({labKey, enabled: false}));
          }
        }
      }
    },
    [dispatch, ldClient, storedLabs]
  );

  return (
    <AuthProvider
      client={authClient}
      navigate={navigate}
      onChange={onAuthChange}
      private
    >
      <QueryClientProvider client={queryClient}>
        <AppLayout />
        <ReactQueryDevtools />
      </QueryClientProvider>
    </AuthProvider>
  );
};

export default SecureApp;
